import { HdAnnotation, HdPoint, HdTextAnnotation } from './hd-annotation.models';

/** Kortste afstand van punt `p` tot het lijnstuk `a`–`b`. */
export function distanceToSegment(p: HdPoint, a: HdPoint, b: HdPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return Math.hypot(p.x - a.x, p.y - a.y);
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Kortste afstand van `p` tot een polyline; `Infinity` bij < 2 punten. */
export function distanceToPolyline(p: HdPoint, points: readonly HdPoint[]): number {
  let min = Infinity;
  for (let i = 1; i < points.length; i++) {
    min = Math.min(min, distanceToSegment(p, points[i - 1], points[i]));
  }
  // Eén punt: afstand tot dat punt; nul punten: Infinity.
  if (points.length === 1) {
    return Math.hypot(p.x - points[0].x, p.y - points[0].y);
  }
  return min;
}

/** Bouwt een `M x y L x y …`-padstring. Leeg bij geen punten. */
export function buildFreehandPathD(points: readonly HdPoint[]): string {
  if (points.length === 0) {
    return '';
  }
  const [head, ...rest] = points;
  return `M ${head.x} ${head.y}` + rest.map(pt => ` L ${pt.x} ${pt.y}`).join('');
}

/**
 * Houdt het eerste punt, daarna enkel punten die minstens `minDist` van het
 * laatst-behouden punt liggen, plus altijd het laatste punt (zodat de streek
 * eindigt waar de pointer losliet). Houdt de DOM-padstring licht.
 */
export function simplifyPoints(points: readonly HdPoint[], minDist: number): HdPoint[] {
  if (points.length <= 2) {
    return [...points];
  }
  const out: HdPoint[] = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const last = out[out.length - 1];
    if (Math.hypot(points[i].x - last.x, points[i].y - last.y) >= minDist) {
      out.push(points[i]);
    }
  }
  out.push(points[points.length - 1]);
  return out;
}

/** As-uitgelijnde bounding box van een tekst-annotatie (ruwe schatting). */
export function textHitBox(ann: HdTextAnnotation): { minX: number; minY: number; maxX: number; maxY: number } {
  // ~0.6× fontSize gemiddelde tekenbreedte is een prima benadering voor hit-test.
  const width = Math.max(1, ann.text.length) * ann.fontSize * 0.6;
  return { minX: ann.at.x, minY: ann.at.y, maxX: ann.at.x + width, maxY: ann.at.y + ann.fontSize };
}

/** True wanneer `p` binnen `threshold` van de annotatie valt. */
export function hitTestAnnotation(p: HdPoint, ann: HdAnnotation, threshold: number): boolean {
  switch (ann.kind) {
    case 'free':
      return distanceToPolyline(p, ann.points) <= threshold + ann.width / 2;
    case 'arrow':
      return distanceToSegment(p, ann.from, ann.to) <= threshold + ann.width / 2;
    case 'text': {
      const b = textHitBox(ann);
      return p.x >= b.minX - threshold && p.x <= b.maxX + threshold
        && p.y >= b.minY - threshold && p.y <= b.maxY + threshold;
    }
  }
}

/** Geeft een nieuwe annotatie waarvan alle coördinaten met (`dx`,`dy`) verschoven zijn. */
export function translateAnnotation(ann: HdAnnotation, dx: number, dy: number): HdAnnotation {
  switch (ann.kind) {
    case 'free':
      return { ...ann, points: ann.points.map(pt => ({ x: pt.x + dx, y: pt.y + dy })) };
    case 'arrow':
      return { ...ann, from: { x: ann.from.x + dx, y: ann.from.y + dy }, to: { x: ann.to.x + dx, y: ann.to.y + dy } };
    case 'text':
      return { ...ann, at: { x: ann.at.x + dx, y: ann.at.y + dy } };
  }
}
